import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useThreads, useCreateThread } from '@/data/messaging';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Empty } from '@/components/ui/Empty';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/parent/messages/')({
  component: ParentMessages,
});

function ParentMessages() {
  const navigate = useNavigate();
  const threads = useThreads();
  const createThread = useCreateThread();
  const [showNew, setShowNew] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  async function handleNewThread() {
    const userResp = await supabase.auth.getUser();
    const familyId = userResp.data.user?.app_metadata.family_id as string | undefined;
    if (!familyId) {
      toast({
        title: "Couldn't start a message",
        description: 'Your account is not linked to a family yet.',
        variant: 'error',
      });
      return;
    }
    try {
      const t = await createThread.mutateAsync({
        family_id: familyId,
        subject: subject.trim(),
        firstMessage: body.trim(),
      });
      setSubject('');
      setBody('');
      setShowNew(false);
      await navigate({ to: '/parent/messages/$threadId', params: { threadId: t.id } });
    } catch (err) {
      toast({
        title: 'Send failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
            Messages
          </p>
          <h1 className="mt-2">Studio inbox</h1>
        </div>
        <Button onClick={() => setShowNew((v) => !v)}>{showNew ? 'Cancel' : 'New message'}</Button>
      </div>

      {showNew && (
        <section aria-labelledby="new-message-heading" className="surface-elevated space-y-4 p-5">
          <h2 id="new-message-heading" className="sr-only">
            Compose new message
          </h2>
          <div>
            <Label htmlFor="m-subject">Subject</Label>
            <Input
              id="m-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="What can the studio help with?"
              required
            />
          </div>
          <div>
            <Label htmlFor="m-body">Message</Label>
            <textarea
              id="m-body"
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 focus-visible:outline-none"
              required
            />
          </div>
          <Button
            onClick={() => void handleNewThread()}
            disabled={!subject.trim() || !body.trim() || createThread.isPending}
          >
            {createThread.isPending ? 'Sending…' : 'Send'}
          </Button>
        </section>
      )}

      {threads.isLoading && <p aria-live="polite">Loading…</p>}

      {threads.data?.length === 0 && !showNew && (
        <Empty
          title="No messages yet"
          description="Reach out to the studio with a question, a schedule conflict, or a billing concern."
          action={<Button onClick={() => setShowNew(true)}>Start a conversation</Button>}
        />
      )}

      {threads.data && threads.data.length > 0 && (
        <ul className="grid gap-2">
          {threads.data.map((t) => (
            <li key={t.id} className="surface-elevated">
              <Link
                to="/parent/messages/$threadId"
                params={{ threadId: t.id }}
                className="block p-4 hover:bg-[var(--color-surface-sunken)] focus-visible:outline-none"
              >
                <div className="flex items-baseline justify-between">
                  <h3 className="font-medium">{t.subject}</h3>
                  <p className="text-[var(--color-ink-subtle)] text-[var(--text-xs)] tabular-nums">
                    {new Date(t.last_message_at).toLocaleDateString()}
                  </p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
